/**
 * Admin Agents
 *
 * Dashboard showing all house agents with status, capabilities,
 * health indicators, and task counts.
 *
 * @module @humanizer/studio/components/admin/AdminAgents
 */

import { useState, useEffect } from 'react';
import { useApi, type AdminAgentStatus } from '../../contexts/ApiContext';

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function AdminAgents() {
  const api = useApi();
  const [agents, setAgents] = useState<AdminAgentStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAgents();
  }, []);

  async function loadAgents() {
    setLoading(true);
    setError(null);
    try {
      const result = await api.admin.listAgents();
      setAgents(result.agents);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agents');
    } finally {
      setLoading(false);
    }
  }

  function getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'idle': return 'admin-badge admin-badge--success';
      case 'working': return 'admin-badge admin-badge--warning';
      case 'error': return 'admin-badge admin-badge--danger';
      case 'disabled': return 'admin-badge admin-badge--muted';
      default: return 'admin-badge';
    }
  }

  function getHealthBadgeClass(health: string): string {
    switch (health) {
      case 'healthy': return 'admin-badge admin-badge--success';
      case 'degraded': return 'admin-badge admin-badge--warning';
      case 'unhealthy': return 'admin-badge admin-badge--danger';
      default: return 'admin-badge';
    }
  }

  function formatLastActive(ts: number): string {
    if (!ts) return 'Never';
    const diff = Date.now() - ts;
    if (diff < 60_000) return 'Just now';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    return `${Math.floor(diff / 86_400_000)}d ago`;
  }

  if (loading) {
    return (
      <div className="admin-page">
        <div className="admin-page__header">
          <h1 className="admin-page__title">House Agents</h1>
        </div>
        <div className="admin-loading">Loading agents...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <div className="admin-page__header">
          <h1 className="admin-page__title">House Agents</h1>
        </div>
        <div className="admin-error">
          <p>{error}</p>
          <button className="admin-btn admin-btn--primary" onClick={loadAgents}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const totalCompleted = agents.reduce((sum, a) => sum + a.health.tasksCompleted, 0);
  const totalFailed = agents.reduce((sum, a) => sum + a.health.tasksFailed, 0);
  const healthyCount = agents.filter(a => a.health.status === 'healthy').length;

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1 className="admin-page__title">House Agents</h1>
        <p className="admin-page__subtitle">
          {agents.length} agents &middot; {healthyCount} healthy &middot;{' '}
          {totalCompleted} tasks completed &middot; {totalFailed} failed
        </p>
        <button className="admin-btn admin-btn--secondary" onClick={loadAgents}>
          Refresh
        </button>
      </div>

      <div className="admin-agents-grid">
        {agents.map((agent) => (
          <div key={agent.id} className="admin-card">
            <div className="admin-card__header">
              <div>
                <h3 className="admin-card__title">{agent.name}</h3>
                <span className="admin-card__subtitle">{agent.house}</span>
              </div>
              <div className="admin-card__badges">
                <span className={getStatusBadgeClass(agent.status)}>
                  {agent.status}
                </span>
                <span className={getHealthBadgeClass(agent.health.status)}>
                  {agent.health.status}
                </span>
              </div>
            </div>

            <div className="admin-card__body">
              <div className="admin-card__stats">
                <div className="admin-card__stat">
                  <span className="admin-card__stat-value">{agent.health.tasksCompleted}</span>
                  <span className="admin-card__stat-label">Completed</span>
                </div>
                <div className="admin-card__stat">
                  <span className="admin-card__stat-value">{agent.health.tasksFailed}</span>
                  <span className="admin-card__stat-label">Failed</span>
                </div>
                <div className="admin-card__stat">
                  <span className="admin-card__stat-value">
                    {formatLastActive(agent.health.lastActive)}
                  </span>
                  <span className="admin-card__stat-label">Last Active</span>
                </div>
              </div>

              <div className="admin-card__capabilities">
                {agent.capabilities.map((cap) => (
                  <span key={cap} className="admin-tag">{cap}</span>
                ))}
              </div>

              {agent.health.errorMessage && (
                <div className="admin-card__error">
                  {agent.health.errorMessage}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
